<template>
  <b-modal id="form-modal"
           :size="options && options.modalsize ? options.modalsize : 'md'"
           :title="modalTitle"
           :data-model="options ? options.model : ''"
           @hide="onHide"
           no-enforce-focus
           no-close-on-backdrop
           centered>
    <component v-if="options && options.additionalFormComponent"
               :options="options"
               :is="formcomponents[options.additionalFormComponent]"
               :object="object"/>
    <component v-if="options && options.formComponent"
               :options="options"
               @save="save"
               :is="formcomponents[options.formComponent]"
               :edit="edit"
               :selectModels="selectModels"
               :object="object"/>
    <div v-if="options && !options.formComponent && prepared"
         :class="{ invisible: !prepared }"
         class="row">
      <template v-for="(f, find) in options.fields">
        <div :key="'form-field-'+find"
             v-if="elementShouldBeShown(f)"
             :class="[f.form && f.form.classes ? f.form.classes : 'col-12', {
               'd-none': f.form && f.form.full_card && !fullCard
             }]">
          <VariableInput :field="f"
                         :object="object"
                         :edit="edit"
                         @enter="save"
                         @change="inputChanged(f)"
                         :selectModels="selectModels"/>
          <ValidationError v-if="!f.form?.hideStandardValidation"
                           :validationId="f.validation_id || f.id"/>
        </div>
      </template>
    </div>
    <component v-if="options && options.additionalFormComponentBottom"
               :options="options"
               :is="formcomponents[options.additionalFormComponentBottom]"
               :object="object"/>
    <template #modal-footer="{ close }">
      <div class="modal-footer-buttons">
        <b-button variant="themed"
                  v-if="modalMode === 'show'"
                  @click="close">
          {{ $t('close') }}
        </b-button>
        <b-button variant="themed"
                  class="cancel"
                  @click="close"
                  v-if="modalMode === 'edit'">
          {{ $t('cancel') }}
        </b-button>
        <b-button variant="themed"
                  class="ml-2"
                  :disabled="loading"
                  v-if="modalMode === 'edit'"
                  @click="save">
          {{ $t('save') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import VariableInput from "@/components/form/VariableInput"
import { EloquentService } from "@/services/api/eloquent.api.service"
import ValidationError from "@/components/form/ValidationError"
import {appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import {getOptionsFromFile} from "@/services/eloquent.helper"
import {computed, getCurrentInstance, nextTick, onMounted, ref, watch} from "vue"
import {useStore} from "vuex"
import i18n from "@/lang/i18n"
import { asyncComponentsFromContext } from "@/services/components.helper"

const store = useStore()
const instance = getCurrentInstance()

const options = ref(null)
const prepared = ref(false)
let oldObject = {}

const model = computed(() => store.state.datatable.viewModel)
const errors = computed(() => store.state.datatable.validationErrors)
const eloquentOptions = computed(() => store.state.datatable.eloquentOptions)
const selectModels = computed(() => store.state.datatable.selectModels)
const loading = computed(() => store.state.dom.loading)
const object = computed(() => store.state.datatable.selectedModel)
const datatableModule = computed(() => store.state.datatable.module)
const fullCard = computed(() => store.state.datatable.fullCard)
const modalMode = computed(() => store.state.datatable.modalMode)
const dataTableOptionsPath = computed(() => store.state.datatable.dataTableOptionsPath)
const rights = computed(() => store.state.auth.rights)
const superadmin = computed(() => store.state.auth.superadmin)
const modelName = computed(() => store.getters.modelName)

const edit = computed(() => !!object.value[options.value.primaryKey])

const prepareParams = computed(() => {
  let params = {}
  if(!options.value || !options.value.prepareParams || options.value.prepareParams.length < 1) {
    return params
  }
  let ids = options.value.prepareParams

  ids.forEach(item => {
    params[item] = object.value[item]
  })
  return params
})

const formcomponents = computed(() => {
  return asyncComponentsFromContext(require.context(`@/components/parts/datatable/form/`, true, /\.vue$/i, 'lazy'))
})

const modalTitle = computed(() => {
  if(options.value?.modaltitle) {
    if(typeof options.value.modaltitle == 'function') {
      return i18n.global.t(options.value.modaltitle(store, object.value))
    }
    return i18n.global.t(options.value.modaltitle)
  }
  return modelName.value ? i18n.global.t(modelName.value.toLowerCase()) : ''
})

const save = async () => {
  if(loading.value) return
  const id = object.value[options.value.primaryKey]
  let formData = new FormData()
  formData = appendFormdata(formData, options.value.adapter ? options.value.adapter(object.value) : object.value)
  if(id || id === 0) {
    try {
      const res = await EloquentService.update(model.value, id, formData, superadmin.value)
      finished(res.data)
    } catch (e) {
      console.log(e)
    }
  }
  if(!id && id !== 0) {
    // we load here again, cause there might be several eloquent objects on one page and options are loaded from the object which is not added/edited currently
    const specificOptions = await getOptionsFromFile(datatableModule.value, modelName.value, dataTableOptionsPath.value)
    try {
      let res
      if(specificOptions?.customCreateFunction) {
        // third argument used to be the component instance (`this`) - custom functions use its $noty, $t and $store
        res = await specificOptions?.customCreateFunction(object.value, superadmin.value, instance.proxy)
      } else {
        res = await EloquentService.create(model.value, formData, superadmin.value)
      }
      finished(res.data)
    } catch (e) {
      console.log(e)
      if(eloquentOptions.value?.showValidationErrorsNoty) {
        const errorsArray = Object.values(e?.response?.data?.errors || {})
        const text = errorsArray.join('<br/>')
        if(text) {
          instance.proxy.$noty.error(text)
        }
      }
      console.log(e?.response?.data?.errors || e?.response?.data || e?.response || e)
    }
  }
}

const finished = (newObject) => {
  instance.proxy.$noty.info(i18n.global.t('success_message'))
  instance.proxy.$bvModal.hide('form-modal')
  store.commit('incrementDataTableCounter')
  newObject.model_name = model.value
  store.commit('setNewObject', newObject)
}

const parseOptions = async () => {
  options.value = await getOptionsFromFile(datatableModule.value, modelName.value, dataTableOptionsPath.value)
  await loadSelectModels()
}

const loadSelectModels = async () => {
  if(!options.value) {
    store.commit("setSelectModels", {})
    return
  }
  try {
    const res = await EloquentService.prepare(model.value, prepareParams.value, superadmin.value)
    store.commit("setSelectModels", Array.isArray(res?.data?.options) ? {} : res?.data?.options)
    if(res?.data?.model) {
      store.commit('setSelectedModel', res.data.model)
    }
    prepared.value = true
    await nextTick()
    instance.proxy.$bvModal.show('form-modal')
  } catch (e) {
    console.log(e)
  }
}

const onHide = () => {
  store.commit("setValidationErrors", {})
  store.commit("setShowModal", false)
  store.commit("setFullCard", true)
  prepared.value = false
  if(eloquentOptions.value) { // returning to previous model if this add form was custom
    store.commit('setViewModel', eloquentOptions.value.model)
  }
  store.commit('setModalMode', 'edit')
  store.commit('setLastChangedField', null)
}

const elementShouldBeShown = (f) => {
  if(!f.form) return false
  if(f.form.hidden) return false
  if(!edit.value && f.unavailableoncreate) return false
  if(edit.value && f.unavailableonedit) return false
  if(f.right_excludes && rights.value.includes(f.right_excludes)) return false
  const tabs = f.form.tabs || (f.form.tab ? [f.form.tab] : null)
  if(tabs && store.state.datatable.formTabActive && !tabs.includes(store.state.datatable.formTabActive)) return false
  return meetsCondition(f)
}

const meetsCondition = (field) => {
  if(field.form.conditionFunc) {
    return field.form.conditionFunc(store, object.value)
  }
  return true
}

const inputChanged = (f) => {
  store.commit('setLastChangedField', copyObject(f))
}

watch(object, (val) => {
  var tmp_errors = copyObject(errors.value)
  for (let key in val) {
    if ({}.hasOwnProperty.call(val, key)) {
      if(!oldObject[key] || val[key] != oldObject[key]) {
        delete tmp_errors[key]
      }
    }
  }
  store.commit("setValidationErrors", tmp_errors)
  oldObject = copyObject(val)
}, { deep: true })

onMounted(() => {
  parseOptions()
})
</script>
